import { message as messageToast } from "@/atoms"
import { ERROR_EVENTS } from "@/const/event.constants"
import {
  isAuthorizationError,
  isClientError,
  isNotFoundError
} from "@/helpers/error.helper"
import { isSelfHosted } from "@/helpers/self-hosted.helper"
import useServices from "@/hooks/useServices"
import { useTranslation } from "@/hooks/useTranslation"
import {
  Query,
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient
} from "@tanstack/react-query"
import { useEffect } from "react"

import type { Services } from "@/interfaces/services"
import type {
  ConfigLoadMore,
  FetchError,
  GetUseMutationOptions,
  HttpQuerySettings,
  KeysPredicate,
  LoadMoreResult,
  MutationSettings,
  QueryClient,
  QueryFunctionContext,
  QueryKeyT,
  QueryOptions,
  ResponseLoadMore,
  Updater,
  UseQueryOptions
} from "./useHttp.types"

export { useMutation, useQueryClient } from "@tanstack/react-query"
export type {
  Config,
  ConfigInfinityQuery,
  GetUseMutationOptions,
  LoadMoreResult,
  Params,
  QueryClient,
  Updater
} from "./useHttp.types"

export const STALE_TIME = 1000 * 60 * 5 // 5 minutes
export const CACHE_TIME = 1000 * 60 * 10 // 10 minutes

export const fetcher = <T>(
  {
    queryKey,
    headers
  }: QueryFunctionContext & { headers?: Record<string, string> },
  services: { http: Services.IHttpAdapter }
): Promise<T> => {
  const { http } = services
  const [url, params] = queryKey

  return http.get<T>(url, { params: { ...params }, headers })
}

const toastErrorRef = {} as Record<string, boolean>

export const onRetry = (failureCount: number, error: unknown) => {
  if (isClientError(error)) return false

  return failureCount < 3
}

export const useHttp = <TQueryData, TData = TQueryData>(
  url: string | null,
  params?: object,
  config?: Omit<QueryOptions<TQueryData, Error, TData>, "queryKey"> &
    HttpQuerySettings
) => {
  const { t } = useTranslation()
  const {
    headers,
    silentErrorStatuses,
    silentErrors,
    preventLogStatuses,
    ...restConfig
  } = config || {}
  const services = useServices()
  const { data, error, ...context } = useQuery<
    TQueryData,
    Error,
    TData,
    QueryKeyT
  >({
    queryKey: [url!, params],
    queryFn: ({ queryKey, signal, client }) =>
      fetcher<TQueryData>(
        { queryKey, meta: undefined, headers, signal, client },
        services
      ),
    gcTime: CACHE_TIME,
    staleTime: STALE_TIME,
    throwOnError: (error) => isAuthorizationError(error),
    retry: onRetry,
    ...restConfig
  })

  useEffect(() => {
    const { isError } = context

    if (!isError || !error) return

    const selfHosted = isSelfHosted()
    const { logger } = services
    const {
      status,
      statusText,
      message = selfHosted
        ? t("selfHosted.serverGeneralError")
        : t("error.serverGeneralError")
    } = (error as FetchError) || {}

    const showToast = (message: string) => {
      try {
        const toastKey = `${url}_${JSON.stringify(params)}_${message}`
        const toastExist = toastErrorRef[toastKey]

        if (toastExist) return

        toastErrorRef[toastKey] = true

        messageToast.error(message).then(() => {
          delete toastErrorRef[toastKey]
        })
      } catch {
        messageToast.error(message)
      }
    }

    // Like the mutations: show the API detail for a 4xx, never for a 5xx.
    const detail = (error as any)?.data?.detail
    const reason =
      isClientError(error) && typeof detail === "string" ? detail : ""

    // The caller shows these statuses on the page; they are still logged.
    if (!silentErrors && !silentErrorStatuses?.includes(status)) {
      if (isNotFoundError(error)) showToast(t("error.notFoundGeneralError"))
      else if (!isAuthorizationError(error))
        showToast(reason || error?.message || statusText || message)
    }

    if (!preventLogStatuses?.includes(status)) {
      logger.error(error, {
        url,
        params,
        extra: {
          params,
          statusText
        },
        tags: {
          url,
          status,
          custom_error: ERROR_EVENTS.NETWORK.ERROR_RECEIVING_DATA_FROM_NETWORK
        },
        fingerprint: [message, "useHttp"]
      })
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context.isError])

  return { response: data, error: error as FetchError, ...context }
}

export const useParallelQueries = <T = unknown>(
  urls: (
    string | { url: string; params: Record<string, unknown>; enabled?: boolean }
  )[]
) => {
  const services = useServices()

  const context = useQueries({
    queries: urls.map<UseQueryOptions<T, Error>>((value) => {
      const isValueString = typeof value === "string"
      const params = isValueString ? {} : value.params
      const url = isValueString ? value : value.url
      const enabled = isValueString || value.enabled !== false ? true : false

      return {
        staleTime: STALE_TIME,
        gcTime: CACHE_TIME,
        queryKey: [url, params],
        queryFn: ({ signal, client }) =>
          fetcher(
            { client, signal, queryKey: [url, params], meta: undefined },
            services
          ),
        retry: onRetry,
        enabled
      }
    })
  })

  return [
    context,
    {
      isLoading: context.some((query) => query.isLoading),
      isPending: context.some((query) => query.isPending)
    }
  ] as const
}

const useGenericMutation = <T, S>(
  mutationFn: (data: T | S) => Promise<T>,
  url: string,
  params?: object,
  updater?: ((oldData: T, newData: S, previousData: T) => T) | undefined,
  getOptions?: GetUseMutationOptions<T, S>
) => {
  const queryClient = useQueryClient()

  return useMutation<T, Error, T | S>({
    mutationFn,
    onMutate: async (data) => {
      await queryClient.cancelQueries({ queryKey: [url!, params] })

      const previousData = queryClient.getQueryData([url!, params])

      queryClient.setQueryData<T>([url!, params], (oldData) => {
        return updater
          ? updater(oldData!, data as S, previousData as T)
          : (oldData as T)
      })

      return { previousData }
    },
    onError: (_err, _, context: any) => {
      queryClient.setQueryData([url!, params], context?.previousData)
    },
    onSettled: () => {
      return queryClient.invalidateQueries({ queryKey: [url] })
    },
    ...getOptions?.(queryClient)
  })
}

export const useUpdate = <T, S>(
  url: string,
  params?: object,
  updater?: (oldData: T, newData: S) => T,
  options?: GetUseMutationOptions<T, S>,
  settings?: MutationSettings
) => {
  const { http } = useServices()

  return useGenericMutation<T, S>(
    (data) => {
      const { needle, data: body } = (data || {}) as any

      if (needle && body) {
        return http.put(`${url}${needle}`, body, settings)
      }

      return http.put(url, data, settings)
    },
    url,
    params,
    updater,
    options
  )
}

export const usePatch = <T, S>(
  url: string,
  params?: object,
  updater?: (oldData: T, newData: S) => T,
  options?: GetUseMutationOptions<T, S>
) => {
  const { http } = useServices()

  return useGenericMutation<T, S>(
    (data) => {
      const { needle, data: body } = (data || {}) as any

      if (needle && body) {
        return http.patch(`${url}${needle}`, body)
      }

      return http.patch(url, data)
    },
    url,
    params,
    updater,
    options
  )
}

export const useCreate = <T, S>(
  url: string,
  params?: object,
  updater?: (oldData: T, newData: S) => T,
  options?: GetUseMutationOptions<T, S>,
  settings?: MutationSettings
) => {
  const { http } = useServices()

  return useGenericMutation<T, S>(
    (data) => {
      const { needle, data: body } = (data || {}) as any

      if (needle && body) {
        return http.post(`${url}${needle}`, body, settings)
      }

      return http.post(url, data, settings)
    },
    url,
    params,
    updater,
    options
  )
}

export const useDelete = <T, S = T>(
  url: string,
  params?: object,
  updater?: Updater<T, S>,
  options?: GetUseMutationOptions<T, S>
) => {
  const { http } = useServices()

  return useGenericMutation<T, S>(
    (needle?: any) => {
      const actualNeedle =
        typeof needle === "string" ? needle : (needle as any)?.needle
      const body =
        typeof needle === "object" ? (needle as any)?.data : undefined

      // needles come with or without their leading slash; useCreate's start with one
      const path = actualNeedle
        ? `/${String(actualNeedle).replace(/^\/+/, "")}`
        : ""

      return http.delete(`${url}${path}`, {}, body)
    },
    url,
    params,
    updater,
    options
  )
}

export const useLoadMore = <TData>(
  url: string,
  params: { [x: string]: unknown },
  config: ConfigLoadMore<TData> = { initialPageParam: 0 }
) => {
  const { http } = useServices()
  const { initialPageParam = 0, ...restConfig } = config

  const result = useInfiniteQuery<
    ResponseLoadMore<TData>,
    unknown,
    LoadMoreResult<TData>,
    string[],
    number
  >({
    staleTime: STALE_TIME,
    gcTime: CACHE_TIME,
    queryKey: [url, JSON.stringify(params)],
    initialPageParam,
    queryFn: ({ pageParam }) => {
      return http.get<ResponseLoadMore<TData>>(url, {
        params: { pageNumber: `${pageParam}`, pageSize: "20", ...params }
      })
    },
    retry: onRetry,
    getNextPageParam: (lastPage) =>
      lastPage?.metadata?.hasNext ? lastPage.metadata.page + 1 : undefined,
    select: ({ pages, pageParams }) => ({
      pageParams,
      pages: pages.flatMap((page) => page.data),
      metadata: pages[pages.length - 1]?.metadata
    }),
    ...restConfig
  })

  return result
}

const includesKeysPredicate =
  (include: KeysPredicate, exclude?: KeysPredicate) => (query: Query) => {
    const queryKey = query?.queryKey?.[0]

    if (typeof queryKey === "string") {
      const excludeCondition =
        exclude?.every((key) => !queryKey.includes(key)) ?? true

      return excludeCondition && include.every((key) => queryKey.includes(key))
    }

    return false
  }

const onMutateWithPredicate =
  (
    queryClient: QueryClient,
    { include, exclude }: { include: KeysPredicate; exclude?: KeysPredicate }
  ) =>
  async (data: { needle: string; data: Record<string, unknown> }) => {
    const predicate = includesKeysPredicate(include, exclude)

    await queryClient.cancelQueries({ predicate })

    const affectedQueries = queryClient.getQueryCache().findAll({ predicate })
    const previousStates = affectedQueries.map((query) => ({
      queryKey: query.queryKey,
      data: query.state.data
    }))
    const { data: nextRecord } = data

    for (const { queryKey } of previousStates) {
      queryClient.setQueryData(
        queryKey,
        (response: KosmosApi.Response<Record<string, unknown>[]>) => {
          const { data, ...rest } = response

          return {
            data: data.map((record) =>
              record.id === nextRecord.id
                ? { ...record, ...nextRecord }
                : record
            ),
            ...rest
          }
        }
      )
    }

    return { previousStates }
  }

const onErrorWithPredicate =
  (queryClient: QueryClient) => (_: Error, __: any, context: any) => {
    if (!context?.previousStates) return

    for (const { queryKey, data } of context.previousStates) {
      queryClient.setQueryData(queryKey, data)
    }
  }

export const predicateHelpers = {
  includesKeysPredicate,
  onMutate: onMutateWithPredicate,
  onError: onErrorWithPredicate
}
